"use client";

import * as SheetPrimitive from "@radix-ui/react-dialog";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { XIcon } from "lucide-react";
import type * as React from "react";
import { cn } from "../lib";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

export type SheetPositioning = "viewport" | "container";

const sheetPositioningClass: Record<SheetPositioning, string> = {
	viewport: "fixed",
	container: "absolute",
};

const SheetPortal = ({
	container,
	...props
}: SheetPrimitive.DialogPortalProps) => (
	<SheetPrimitive.Portal container={container} {...props} />
);

const SheetOverlay = ({
	className,
	positioning = "viewport",
	...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay> & {
	positioning?: SheetPositioning;
}) => (
	<SheetPrimitive.Overlay
		className={cn(
			"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-[200] bg-background/80 backdrop-blur-xs data-[state=closed]:animate-out data-[state=open]:animate-in",
			sheetPositioningClass[positioning],
			positioning === "viewport" ? "inset-0" : "inset-0",
			className,
		)}
		{...props}
	/>
);

const sheetVariants = cva(
	"z-[200] gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",
	{
		variants: {
			positioning: {
				viewport: "fixed",
				container: "absolute",
			},
			side: {
				top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
				bottom: "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
				left: "inset-y-0 left-0 h-full w-full max-w-[375px] border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
				right: "inset-y-0 right-0 h-full w-full max-w-[375px] border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
			},
		},
		defaultVariants: {
			positioning: "viewport",
			side: "right",
		},
	},
);

type SheetContentProps = {} & React.ComponentProps<
	typeof SheetPrimitive.Content
> &
	VariantProps<typeof sheetVariants> & {
		/** Merged into the sheet overlay (e.g. raise z-index for a specific host). */
		overlayClassName?: string;
		/**
		 * When true, omit the default top-right close control (e.g. when an external
		 * trigger already toggles open/closed).
		 */
		hideClose?: boolean;
		/** Portal target; omit for `document.body`. */
		portalContainer?: HTMLElement | null;
		/** `viewport` (default): fixed full-window. `container`: absolute within host. */
		positioning?: SheetPositioning;
		/** When true, render content only (no bundled portal/overlay). */
		bare?: boolean;
	};

function SheetCloseButton({ hideClose }: { hideClose: boolean }) {
	if (hideClose) {
		return null;
	}
	return (
		<SheetPrimitive.Close
			className={cn(
				"absolute top-4 right-4 z-[210] inline-flex size-10 cursor-pointer items-center justify-center rounded-full shadow-lg backdrop-blur",
				"border border-black/10 bg-black/5 text-foreground/80",
				"transition-colors hover:bg-black/10 hover:text-foreground",
				"focus:outline-none focus:ring-2 focus:ring-black/25 focus:ring-offset-2 focus:ring-offset-background",
				"dark:border-white/10 dark:bg-white/5 dark:text-white/80",
				"dark:hover:bg-white/10 dark:hover:text-white",
				"dark:focus:ring-white/50 dark:focus:ring-offset-black/30",
				"disabled:pointer-events-none",
			)}
		>
			<XIcon className="size-4" aria-hidden />
			<span className="sr-only">Close</span>
		</SheetPrimitive.Close>
	);
}

/** Sheet panel surface without portal/overlay — compose with {@link SheetPortal}. */
const SheetPanel = ({
	side = "right",
	positioning = "viewport",
	className,
	children,
	hideClose = false,
	...props
}: Omit<
	SheetContentProps,
	"overlayClassName" | "portalContainer" | "bare"
>) => (
	<SheetPrimitive.Content
		className={cn(sheetVariants({ side, positioning }), className)}
		{...props}
	>
		{children}
		<SheetCloseButton hideClose={hideClose} />
	</SheetPrimitive.Content>
);

const SheetContent = ({
	side = "right",
	positioning = "viewport",
	className,
	overlayClassName,
	portalContainer,
	children,
	hideClose = false,
	bare = false,
	...props
}: SheetContentProps) => {
	const panel = (
		<SheetPanel
			side={side}
			positioning={positioning}
			className={className}
			hideClose={hideClose}
			{...props}
		>
			{children}
		</SheetPanel>
	);

	if (bare) {
		return panel;
	}

	return (
		<SheetPortal container={portalContainer ?? undefined}>
			<SheetOverlay
				className={overlayClassName}
				positioning={positioning}
			/>
			{panel}
		</SheetPortal>
	);
};

const SheetHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"flex flex-col space-y-2 text-center sm:text-left",
			className,
		)}
		{...props}
	/>
);

const SheetFooter = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"flex flex-row flex-wrap items-center justify-end gap-2",
			className,
		)}
		{...props}
	/>
);

const SheetTitle = ({
	className,
	...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) => (
	<SheetPrimitive.Title
		className={cn(
			"font-black text-foreground text-lg tracking-wide uppercase",
			className,
		)}
		{...props}
	/>
);

const SheetDescription = ({
	className,
	...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) => (
	<SheetPrimitive.Description
		className={cn("text-muted-foreground text-sm", className)}
		{...props}
	/>
);

export {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetOverlay,
	SheetPanel,
	SheetPortal,
	SheetTitle,
	SheetTrigger,
	sheetVariants,
};
